import type {
  InitialEntitySeed,
  KineticCoreConfig,
} from "@cloudnest/kinetic-core-engine";
import type {
  DeploymentLaneInitConfig,
  MatchInitConfig,
  PieceTypeInitConfig,
} from "./types";

export interface ResolvedInitConfig {
  readonly source: MatchInitConfig;
  readonly coreConfig: KineticCoreConfig;
  readonly initialEntities: ReadonlyArray<InitialEntitySeed>;
  readonly pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>;
}

function assertNonEmptyPieceTypes(
  pieceTypes: ReadonlyArray<PieceTypeInitConfig>,
) {
  if (pieceTypes.length === 0) {
    throw new Error("init-config: at least one piece type is required");
  }
}

function buildPieceTypeMap(
  pieceTypes: ReadonlyArray<PieceTypeInitConfig>,
): ReadonlyMap<string, PieceTypeInitConfig> {
  const map = new Map<string, PieceTypeInitConfig>();

  for (const pieceType of pieceTypes) {
    if (map.has(pieceType.id)) {
      throw new Error(`init-config: duplicate piece type id '${pieceType.id}'`);
    }

    if (pieceType.count <= 0) {
      throw new Error(
        `init-config: piece type '${pieceType.id}' count must be > 0`,
      );
    }

    if (pieceType.size <= 0) {
      throw new Error(
        `init-config: piece type '${pieceType.id}' size must be > 0`,
      );
    }

    if (pieceType.weight <= 0) {
      throw new Error(
        `init-config: piece type '${pieceType.id}' weight must be > 0`,
      );
    }

    map.set(pieceType.id, pieceType);
  }

  return map;
}

function assertLaneWithinBounds(
  lane: DeploymentLaneInitConfig,
  config: MatchInitConfig,
): void {
  for (const col of lane.columns) {
    if (col < 0 || col >= config.board.cols) {
      throw new Error(
        `init-config: lane '${lane.pieceTypeId}' has column ${col} outside board width`,
      );
    }
  }

  if (lane.ownerOneRow < 0 || lane.ownerOneRow >= config.board.rows) {
    throw new Error(
      `init-config: lane '${lane.pieceTypeId}' ownerOneRow ${lane.ownerOneRow} outside board height`,
    );
  }

  if (lane.ownerTwoRow < 0 || lane.ownerTwoRow >= config.board.rows) {
    throw new Error(
      `init-config: lane '${lane.pieceTypeId}' ownerTwoRow ${lane.ownerTwoRow} outside board height`,
    );
  }
}

function createInitialEntities(
  config: MatchInitConfig,
  pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>,
): ReadonlyArray<InitialEntitySeed> {
  const seenLaneByPieceType = new Set<string>();
  const ownerOnePositions = new Set<string>();
  const ownerTwoPositions = new Set<string>();
  const entities: InitialEntitySeed[] = [];

  for (const lane of config.deploymentPattern.lanes) {
    const pieceType = pieceTypesById.get(lane.pieceTypeId);
    if (!pieceType) {
      throw new Error(
        `init-config: deployment lane references unknown piece type '${lane.pieceTypeId}'`,
      );
    }

    if (seenLaneByPieceType.has(lane.pieceTypeId)) {
      throw new Error(
        `init-config: piece type '${lane.pieceTypeId}' appears in multiple lanes`,
      );
    }
    seenLaneByPieceType.add(lane.pieceTypeId);

    assertLaneWithinBounds(lane, config);

    if (lane.columns.length !== pieceType.count) {
      throw new Error(
        `init-config: piece type '${pieceType.id}' count (${pieceType.count}) does not match deployment columns (${lane.columns.length})`,
      );
    }

    lane.columns.forEach((column, index) => {
      const ownerOneKey = `${column}:${lane.ownerOneRow}`;
      const ownerTwoKey = `${column}:${lane.ownerTwoRow}`;

      if (ownerOnePositions.has(ownerOneKey)) {
        throw new Error(
          `init-config: duplicate owner one position at (${column}, ${lane.ownerOneRow})`,
        );
      }
      if (ownerTwoPositions.has(ownerTwoKey)) {
        throw new Error(
          `init-config: duplicate owner two position at (${column}, ${lane.ownerTwoRow})`,
        );
      }

      ownerOnePositions.add(ownerOneKey);
      ownerTwoPositions.add(ownerTwoKey);

      const pieceNumber = index + 1;
      const typeId = pieceType.id.toLowerCase();

      entities.push({
        id: `p1-${typeId}-${pieceNumber}`,
        archetype: pieceType.id,
        ownerIndex: 0,
        x: column,
        y: lane.ownerOneRow,
      });

      entities.push({
        id: `p2-${typeId}-${pieceNumber}`,
        archetype: pieceType.id,
        ownerIndex: 1,
        x: column,
        y: lane.ownerTwoRow,
      });
    });
  }

  for (const pieceTypeId of pieceTypesById.keys()) {
    if (!seenLaneByPieceType.has(pieceTypeId)) {
      throw new Error(
        `init-config: missing deployment lane for piece type '${pieceTypeId}'`,
      );
    }
  }

  return entities;
}

export function resolveInitConfig(source: MatchInitConfig): ResolvedInitConfig {
  assertNonEmptyPieceTypes(source.pieceTypes);

  const pieceTypesById = buildPieceTypeMap(source.pieceTypes);

  const coreConfig: KineticCoreConfig = {
    space: {
      cols: source.board.cols,
      rows: source.board.rows,
      boundaryPadding: source.board.boundaryPadding,
    },
    dynamics: source.dynamics,
    archetypes: source.pieceTypes.map((pieceType) => ({
      archetype: pieceType.id,
      radius: pieceType.size,
      mass: pieceType.weight,
    })),
    maxTurns: source.maxTurns,
  };

  const initialEntities = createInitialEntities(source, pieceTypesById);

  return {
    source,
    coreConfig,
    initialEntities,
    pieceTypesById,
  };
}
